import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import { toast } from "sonner";
import { sowingLifecycleKeys } from "./useSowingLifecycle";
import {
  greenhouseTransfersApi,
  type CreateGreenhouseTransferRequestDto,
  type ApproveGreenhouseTransferRequestDto,
  type RejectGreenhouseTransferRequestDto,
} from "../services/greenhouseTransfersApi";

export const GREENHOUSE_TRANSFERS_KEY = ["greenhouseTransfers"] as const;

export const useGreenhouseTransfers = (pageNumber = 1, pageSize = 20) => {
  return useQuery({
    queryKey: [...GREENHOUSE_TRANSFERS_KEY, "all", pageNumber, pageSize],
    queryFn: () => greenhouseTransfersApi.getAll(pageNumber, pageSize),
  });
};

export const useGreenhouseTransferById = (id: string) => {
  return useQuery({
    queryKey: [...GREENHOUSE_TRANSFERS_KEY, id],
    queryFn: () => greenhouseTransfersApi.getById(id),
    enabled: !!id,
  });
};

export const usePendingGreenhouseTransfers = (pageNumber = 1, pageSize = 20) => {
  return useQuery({
    queryKey: [...GREENHOUSE_TRANSFERS_KEY, "pending", pageNumber, pageSize],
    queryFn: () => greenhouseTransfersApi.getPending(pageNumber, pageSize),
  });
};

export const useGreenhouseTransfersByStockEntry = (stockEntryId: string, pageNumber = 1, pageSize = 20) => {
  return useQuery({
    queryKey: [...GREENHOUSE_TRANSFERS_KEY, "by-entry", stockEntryId, pageNumber, pageSize],
    queryFn: () => greenhouseTransfersApi.getByStockEntry(stockEntryId, pageNumber, pageSize),
    enabled: !!stockEntryId,
  });
};

export const useRequestGreenhouseTransfer = () => {
  return useMutation({
    mutationFn: (payload: CreateGreenhouseTransferRequestDto) =>
      greenhouseTransfersApi.request(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...GREENHOUSE_TRANSFERS_KEY] });
      queryClient.invalidateQueries({ queryKey: ["transferWorkflow", "greenhouse"] });
      toast.success("Transfer request submitted for approval");
    },
  });
};

export const useApproveGreenhouseTransfer = () => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ApproveGreenhouseTransferRequestDto }) =>
      greenhouseTransfersApi.approve(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...GREENHOUSE_TRANSFERS_KEY] });
      queryClient.invalidateQueries({ queryKey: ["transferWorkflow", "greenhouse"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouseStock"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
      queryClient.invalidateQueries({ queryKey: [...sowingLifecycleKeys.all, "stock-entries-paged"] });
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.stockEntriesGrowing() });
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.stockEntriesReadyForDispatch() });
      queryClient.invalidateQueries({ queryKey: ["greenhouses"] });
      toast.success("Transfer approved");
    },
  });
};

export const useRejectGreenhouseTransfer = () => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RejectGreenhouseTransferRequestDto }) =>
      greenhouseTransfersApi.reject(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...GREENHOUSE_TRANSFERS_KEY] });
      queryClient.invalidateQueries({ queryKey: ["transferWorkflow", "greenhouse"] });
      toast.success("Transfer request rejected");
    },
  });
};
